import React, { useState, useEffect } from "react";
import { Row, Col, Card, Typography, Button, Image, Tag, Spin, message } from "antd";
import { FilePdfOutlined, VideoCameraOutlined, PictureOutlined, DownloadOutlined, EyeOutlined, FolderAddFilled, FolderFilled } from "@ant-design/icons";
import Meta from "antd/es/card/Meta";
import AWS from "aws-sdk";

const { Title, Paragraph } = Typography;

function Collaterals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Configure AWS
  const configureAWS = () => {
    AWS.config.update({
      accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
      secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
      region: "ap-south-1",
    });
  };

  // Fetch files from S3
  const fetchS3Files = async () => {
    try {
      setLoading(true);
      configureAWS();

      const s3 = new AWS.S3();
      const bucketName = "youvah-cpp-files";
      const folderPrefix = "Collateral Documents/";

      const listParams = {
        Bucket: bucketName,
        Prefix: folderPrefix,
      };

      const listData = await s3.listObjectsV2(listParams).promise();

      // Filter out folder objects and process files
      const files = listData.Contents.filter((file) => file.Key !== folderPrefix && !file.Key.endsWith("/")).map((file) => {
        const fileName = file.Key.split("/").pop();
        const fileExtension = fileName.split(".").pop().toLowerCase();

        // Generate signed URL for the file
        const signedUrl = s3.getSignedUrl("getObject", {
          Bucket: bucketName,
          Key: file.Key,
          Expires: 60 * 60 * 24, // URL expires in 24 hours
        });

        // Determine file type based on extension
        let type = "file";
        if (["pdf"].includes(fileExtension)) {
          type = "pdf";
        } else if (["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(fileExtension)) {
          type = "image";
        } else if (["mp4", "avi", "mov", "wmv", "flv", "webm"].includes(fileExtension)) {
          type = "video";
        }

        return {
          title: fileName.replace(/\.[^/.]+$/, ""), // Remove file extension from title
          type: type,
          src: signedUrl,
          key: file.Key,
          size: file.Size,
          lastModified: file.LastModified,
        };
      });

      setItems(files);
    } catch (error) {
      console.error("Error fetching S3 files:", error);
      message.error("Failed to load collaterals from S3");

      // Fallback to local files if S3 fails
      setItems([
        {
          title: "Partner MOU (PDF)",
          type: "pdf",
          src: "/samples/MOU.pdf",
        },
        {
          title: "PAN Sample (Image)",
          type: "image",
          src: "/samples/pan-sample.svg",
        },
        {
          title: "Aadhaar Sample (Image)",
          type: "image",
          src: "/samples/aadhaar-sample.svg",
        },
        {
          title: "Birth Certificate Sample (Image)",
          type: "image",
          src: "/samples/birth-certificate-sample.svg",
        },
        {
          title: "Program Overview Poster",
          type: "image",
          src: "/logo512.png",
        },
        {
          title: "Student Orientation (Video)",
          type: "video",
          src: "https://www.w3schools.com/html/mov_bbb.mp4",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchS3Files();
  }, []);

  const renderCover = (item) => {
    const base = { height: 100, display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f5f5" };
    return <Image src={item.src} width="100%" height={100} style={{ objectFit: "cover" }} preview />;
    if (item.type === "image") {
      return <Image src={item.src} width="100%" height={100} style={{ objectFit: "cover" }} preview />;
    }
    if (item.type === "video") {
      return (
        <div style={base}>
          <VideoCameraOutlined style={{ fontSize: 40, color: "#555" }} />
        </div>
      );
    }
    if (item.type === "pdf") {
      return (
        <div style={base}>
          <FilePdfOutlined style={{ fontSize: 40, color: "#cf1322" }} />
        </div>
      );
    }
    return (
      <div style={base}>
        <PictureOutlined style={{ fontSize: 40, color: "#555" }} />
      </div>
    );
  };

  const typeToTag = (type) => {
    if (type === "pdf") return <Tag color="red">PDF</Tag>;
    if (type === "video") return <Tag color="geekblue">Video</Tag>;
    if (type === "image") return <Tag color="green">Image</Tag>;
    return <Tag>File</Tag>;
  };

  const renderActions = (item) => {
    const viewHref = item.src;
    const downloadHref = item.src;

    return [
      <a size="small" href={viewHref} target="_blank">
        <EyeOutlined /> View
      </a>,
      <a size="small" type="primary" href={downloadHref} download>
        <DownloadOutlined /> Download
      </a>,
    ];
  };

  if (loading) {
    return (
      <div style={{ padding: 24, background: "#f7f8fa", minHeight: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, background: "#f7f8fa", minHeight: "100%" }}>
      <Row justify="center">
        <Col xs={24} md={22} lg={20} xl={18}>
          <Card bordered style={{ borderRadius: 12, marginBottom: 16 }}>
            <Title level={3} style={{ margin: 0 }}>
              Collaterals
            </Title>
            <Paragraph type="secondary" style={{ marginTop: 8 }}>
              Assets you can share with students: brochures, PDFs, videos, and posters.
            </Paragraph>
          </Card>

          <Row gutter={[16, 16]}>
            {items.map((item) => (
              <Col key={item.key || item.title} xs={24} sm={12} md={12} lg={8}>
                <Card
                  hoverable
                  bordered
                  style={{ height: "100%", border: "1px solid #971eff", padding: "5px" }}
                  cover={renderCover(item)}
                  actions={renderActions(item)}
                >
                  <Meta title={item.title} description={typeToTag(item.type)} />
                </Card>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </div>
  );
}

export default Collaterals;
